import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  viewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import {
  GetCampaignByIdDto,
  GetPhonebookRecipientByScheduledMessageIdDto,
  PhonebookRecipientForUpdateRequest,
  SmsCp,
  SocialMediaSendDetailModel,
  VoiceMessageDetailsRequest,
} from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { RegularExpressions } from 'src/app/core/constants/regular-expressions';
import { ClientDataInitializerService } from 'src/app/core/services/authentication/client-data-initializer.service';
import { DateTimeService } from 'src/app/core/services/date-time.service';
import { AppPrivateNumber } from 'src/app/core/types/app-private-number.type';
import { minNumberValidator } from 'src/app/core/validators/min-number-validator';
import { PhonebookSelectorComponent } from 'src/app/pages/facility/phonebook/phonebook-selector/phonebook-selector.component';
import { SendTypes } from '../../../../core/api/models/send-types';
import { UseTypes } from '../../../../core/api/models/use-types';
import { DynamicTabService } from '../../../../core/services/dynamic-tab.service';
import { UtilityService } from '../../../../core/services/utility.service';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';
import { GetScheduledMessageByIdDto } from './../../../../core/api/models/get-scheduled-message-by-id-dto';
import { SendSubTypes } from './../../../../core/api/models/send-sub-types';
import { SmsSentPeriodType } from './../../../../core/api/models/sms-sent-period-type';
import { PrivateNumbersService } from './../../../../core/api/services/private-numbers.service';
import { ScheduledMessagesService } from './../../../../core/api/services/scheduled-messages.service';
import { SendDateTimeType } from './../../../../core/enums/send-date-time-type.enum';
import { CampaignInfoService } from './../../../../core/services/campaign-info.service';
import {
  DialogConfig,
  DialogService,
  DialogStatus,
} from './../../../../core/services/dialog.service';
import { DateTimeConfig } from './../../../../shared/components/date-time-picker/date-time-picker.component';
import { PeriodicDateTimeConfig } from './../../../../shared/components/periodic-date-time-picker/periodic-date-time-picker.component';
import { PeriodicTypeConfig } from './../../../../shared/components/periodic-type/periodic-type.component';

declare function compressToEncodedURIComponent(input): any;

@Component({
    selector: 'app-base-send-sms',
    templateUrl: './base-send-sms.component.html',
    styleUrls: [
        './base-send-sms.component.scss',
        '../../base/base-sending-system.component.scss',
    ],
    standalone: false
})
export class BaseSendSmsComponent
  extends BaseFormPageComponent
  implements OnInit, OnDestroy
{
  //#region properties

  //html class list
  readonly phonebookSelectorComponent = viewChild(PhonebookSelectorComponent);
  @Input() phonebookRecipientsFinalModel: any[] = [];

  phonebookRecipients: Array<PhonebookRecipientForUpdateRequest> = [];

  @Input() childTagName: string;

  @Input('form') childForm: FormGroup;
  @Input('loading') baseLoading: boolean;
  @Input() config: BaseSendSmsConfig;
  @Input() editModel: GetScheduledMessageByIdDto;
  @Input() formId: string;
  @Input() fastSendLoading: boolean;
  @Input() campaignTitle: string;
  @Input() saveToCampaign: boolean;

  @Output('model') output_model = new EventEmitter<BaseSendSms>();
  @Output('onSubmit') output_onSubmit = new EventEmitter<boolean>();
  @Output('onFastSend') output_onFastSend = new EventEmitter<boolean>();
  @Output('onClosePage') output_onClosePage = new EventEmitter<boolean>();
  @Output('onSaveToCampaign') output_onSaveToCampaign =
    new EventEmitter<boolean>();
  @Output('privateNumber') output_privateNumber = new EventEmitter<string>();

  baseModel: BaseSendSms = {};

  fastNumbers: string;
  isVisibleHelpPage: boolean = false;
  isWrongIndex: boolean = false;

  get SendDateTimeType() {
    return SendDateTimeType;
  }
  get SendTypes() {
    return SendTypes;
  }
  get SmsCp() {
    return SmsCp;
  }

  periodicDateTimeConfig: PeriodicDateTimeConfig;
  directDateTimeConfig: DateTimeConfig;
  gradualDateTimeConfig: DateTimeConfig;
  periodicTypeConfig: PeriodicTypeConfig;

  isShowDirectSend: boolean = true;
  isShowPeriodicSend: boolean = true;
  isShowGradualSend: boolean = true;

  isExpandDirectDateTime: boolean = true;
  isExpandPeriodDateTime: boolean = false;
  isExpandGradualDateTime: boolean = false;

  isShowPhonebook: boolean = false;
  // globalFromIndex: number;
  // globalCount: number;
  //#endregion

  constructor(
    protected currentService: ScheduledMessagesService,
    public dialogService: DialogService,
    public utilityService: UtilityService,
    protected translate: TranslateService,
    public dynamicTabService: DynamicTabService,
    public privateNumberService: PrivateNumbersService,

    protected clientDataService: ClientDataInitializerService
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageVocabs = 'pages.baseSendSms.';
      this.fields = this.pageVocabs + 'fields.';
      this.setComponentsConfig();
      this.setDefaultValue();

      this.getEditingData(this.editModel);

      this.desktop = this.clientDataService.getCurrentClientData?.desktop;
      if (this.config.isCustomMessageInformation) {
        this.form.get('messageInformation').get('smsBody').clearValidators();
        this.form
          .get('messageInformation')
          .get('senderNumberId')
          .clearValidators();
        this.form
          .get('messageInformation')
          .get('smsBody')
          .updateValueAndValidity();
        this.form
          .get('messageInformation')
          .get('senderNumberId')
          .updateValueAndValidity();
      }

      if (this.config.isEdit) {
        this.isExpandPeriodDateTime = true;
        this.isExpandGradualDateTime = true;
        this.onShowAlertInEditMode();
      }

      this.getCampaignData({ ...CampaignInfoService.lastCampaignInfo });
      this.isShowPhonebook = this.editModel?.phonebookRecipients.length > 0;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  ngAfterViewInit(): void {
    //edit mode
    const phonebookSelectorComponent = this.phonebookSelectorComponent();
    if (
      phonebookSelectorComponent &&
      this.phonebookRecipientsFinalModel?.length
    ) {
      phonebookSelectorComponent.phonebookRecipients =
        this.phonebookRecipientsFinalModel;
      phonebookSelectorComponent.applyPhonebookRecipients();
    }
  }

  //#region init

  setComponentsConfig() {
    try {
      this.periodicDateTimeConfig = {
        startDateLabel: 'common.startDay',
        startTimeLabel: 'common.startHour',
        endDateLabel: 'common.endDay',
        endTimeLabel: 'common.endHour',
        startDateFormControl: this.form?.get(
          'sendTypeInformation.sendPeriodDateStart'
        ) as FormControl,
        startTimeFormControl: this.form?.get(
          'sendTypeInformation.sendPeriodTimeStart'
        ) as FormControl,
        endDateFormControl: this.form?.get(
          'sendTypeInformation.sendPeriodDateEnd'
        ) as FormControl,
        endTimeFormControl: this.form?.get(
          'sendTypeInformation.sendPeriodTimeEnd'
        ) as FormControl,
        isConvertToIsoString: this.config.isConvertToIsoString,
        isEditMode: this.formPageConfig.isEditMode,
        minStartDateType: 'today',
        responsiveClassName: 'xl:tw-w-1/2 tw-w-full tw-px-1',
      };
      this.directDateTimeConfig = {
        dateLabel: this.fields + 'sendDay',
        timeLabel: this.fields + 'sendHour',
        dateFormControl: this.form?.get(
          'sendTypeInformation.sendDate'
        ) as FormControl,
        timeFormControl: this.form?.get(
          'sendTypeInformation.sendTime'
        ) as FormControl,
        minDateType: 'today',
        isDefaultValue: true,
        isConvertToIsoString: this.config.isConvertToIsoString,
        isEditMode: this.formPageConfig.isEditMode,
        responsiveClassName: 'xl:tw-w-1/2 tw-w-full tw-px-1',
      };
      this.gradualDateTimeConfig = {
        dateLabel: 'common.startDay',
        timeLabel: 'common.startHour',
        dateFormControl: this.form?.get(
          'sendTypeInformation.sendGradualDateStart'
        ) as FormControl,
        timeFormControl: this.form?.get(
          'sendTypeInformation.sendGradualTimeStart'
        ) as FormControl,
        minDateType: 'today',
        isDefaultValue: true,
        isConvertToIsoString: this.config.isConvertToIsoString,
        isEditMode: this.formPageConfig.isEditMode,
        responsiveClassName: 'xl:tw-w-1/2 tw-w-full tw-px-1',
      };
      this.periodicTypeConfig = {
        periodTypeLabel: this.fields + 'typeSend',
        periodTextLabel: this.fields + 'periodText',
        periodTypeControl: this.form?.get(
          'sendTypeInformation.periodTypeValue'
        ) as FormControl,
        periodTextControl: this.form?.get(
          'sendTypeInformation.periodText'
        ) as FormControl,
        responsiveClassName: 'xl:tw-w-1/2 tw-w-full tw-px-1',
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  setDefaultValue() {
    this.baseModel.isSendWhiteList = false;
    this.baseModel.isFlashSms = false;
    this.baseModel.phonebookRecipients = [];
    this.baseModel.phonebookRecipients = this.editModel?.phonebookRecipients;
    this.onChangeSendType(SendDateTimeType.DateTime);
  }

  initForm() {
    this.form = new FormGroup({
      messageInformation: new FormGroup({
        smsBody: new FormControl('', [
          Validators.required,
          // Validators.pattern(RegularExpressions.smsBodyForbiddenCharacters),
        ]),
        senderNumberId: new FormControl('', Validators.required),
        voiceMessageId: new FormControl(''),
        fastSendSmsBody: new FormControl(''),
        maxTryCount: new FormControl(''),
        minuteBetweenTries: new FormControl(''),
        socialMediaFileId: new FormControl(''),
        socialMediaApplicationId: new FormControl(''),
      }),
      sendTypeInformation: new FormGroup({
        // sendDateTime
        sendDateTimeChecked: new FormControl(),
        sendDate: new FormControl(''),
        sendTime: new FormControl(''),
        // PeriodDateTime
        sendPeriodChecked: new FormControl(''),
        periodTypeValue: new FormControl(''),
        periodText: new FormControl(''),
        sendPeriodDateStart: new FormControl(''),
        sendPeriodTimeStart: new FormControl(''),
        sendPeriodDateEnd: new FormControl(''),
        sendPeriodTimeEnd: new FormControl(''),
        // gradualDateTime
        gradualChecked: new FormControl(''),
        gradualPeriodText: new FormControl(''),
        sendGradualDateStart: new FormControl(''),
        sendGradualTimeStart: new FormControl(''),
        gradualPageSize: new FormControl(''),
        //white List
        description: new FormControl(''),
        saveToCampaign: new FormControl(''),
        isSendWhiteList: new FormControl(''),
        isFlashSms: new FormControl(''),
        extraReceivers: new FormControl(''),
      }),
    });
  }

  changeValidations(event) {
    try {
      this.output_privateNumber.emit(event.number);

      this.form
        .get('messageInformation')
        .get('voiceMessageId')
        .clearValidators();
      this.form.get('messageInformation').get('smsBody').clearValidators();
      this.form.get('messageInformation').get('maxTryCount').clearValidators();
      this.form
        .get('messageInformation')
        .get('minuteBetweenTries')
        .clearValidators();
      this.form
        .get('messageInformation')
        .get('socialMediaFileId')
        .clearValidators();
      // voiceMessageId
      if (this.baseModel.isCanSendVoice) {
        this.form
          .get('messageInformation')
          .get('voiceMessageId')
          .setValidators([Validators.required]);
        this.form
          .get('messageInformation')
          .get('maxTryCount')
          .setValidators([Validators.required]);
        this.form
          .get('messageInformation')
          .get('minuteBetweenTries')
          .setValidators([Validators.required]);
      }
      // social media id
      else if (this.baseModel.isCanSendSocialMedia) {
        this.form
          .get('messageInformation')
          .get('socialMediaFileId')
          .setValidators([Validators.required]);
        this.form
          .get('messageInformation')
          .get('smsBody')
          .setValidators([
            Validators.required,
            // Validators.pattern(RegularExpressions.smsBodyForbiddenCharacters),
          ]);
        // this.form
        //   .get('messageInformation')
        //   .get('socialMediaApplicationId')
        //   .setValidators([Validators.required]);
      }
      // smsBody
      else
        this.form
          .get('messageInformation')
          .get('smsBody')
          .setValidators([
            Validators.required,
            // Validators.pattern(RegularExpressions.smsBodyForbiddenCharacters),
          ]);

      this.form
        .get('messageInformation')
        .get('smsBody')
        .updateValueAndValidity();
      this.form
        .get('messageInformation')
        .get('voiceMessageId')
        .updateValueAndValidity();
      this.form
        .get('messageInformation')
        .get('maxTryCount')
        .updateValueAndValidity();
      this.form
        .get('messageInformation')
        .get('minuteBetweenTries')
        .updateValueAndValidity();
      this.form
        .get('messageInformation')
        .get('socialMediaFileId')
        .updateValueAndValidity();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#endregion

  //#region edit mode

  getEditingData(data: GetScheduledMessageByIdDto) {
    try {
      if (!this.config?.isEdit) return;

      this.config.isShowCampaign = false;
      this.config.isShowExtraReceivers = false;
      this.isShowDirectSend = false;
      this.isShowPeriodicSend = false;
      this.isShowGradualSend = false;
      this.baseModel.smsBody = data.smsText;
      this.baseModel.senderNumberId = data.privateNumberId;
      this.baseModel.description = data.description;
      this.baseModel.voiceMessageId = data.voiceMessageId;
      this.baseModel.maxTryCount = data.maxTryCount;
      this.baseModel.minuteBetweenTries = data.minuteBetweenTries;
      this.baseModel.socialMediaFileId = data.socialMediaFileId;
      this.baseModel.socialMediaFileTitle = data.socialMediaFileTitle;
      this.baseModel.socialMediaApplicationId = data.socialMediaApplicationId;
      this.baseModel.socialMediaApplicationName =
        data.socialMediaApplicationName;
      this.baseModel.socialMediaMessageDetails = {
        socialMediaApplicationId: data.socialMediaApplicationId,
        socialMediaFileId: data.socialMediaFileId,
      };

      if (this.config.justShowDirectDateTimeForEdit) {
        this.isShowDirectSend = true;
        this.directDateTimeConfig.dateTimeValue = data.dateTimeNext;
        this.directDateTimeConfig = Object.assign(
          {},
          this.directDateTimeConfig
        );
        return;
      }

      switch (data.sendSubType) {
        // DirectSend
        case SendSubTypes.Direct:
          this.isShowDirectSend = true;
          this.directDateTimeConfig.dateTimeValue = data.dateTimeNext;
          this.onChangeSendType(SendDateTimeType.DateTime);
          break;
        // PeriodicSend
        case SendSubTypes.Periodic:
          this.isShowPeriodicSend = true;
          this.periodicDateTimeConfig.startDateTimeValue = data.dateTimeStart;
          this.periodicDateTimeConfig.endDateTimeValue = data.dateTimeEnd;
          this.periodicTypeConfig.periodText = data.period;
          this.periodicTypeConfig.periodType = data.periodType;
          this.onChangeSendType(SendDateTimeType.Period);
          break;
        // GradualSend
        case SendSubTypes.Gradual:
          this.isShowGradualSend = true;
          this.gradualDateTimeConfig.dateTimeValue = data.dateTimeNext;
          this.baseModel.gradualPeriodText = data.period;
          this.baseModel.gradualPageSize = data.pageSize;
          this.onChangeSendType(SendDateTimeType.Gradual);
          break;
      }

      //refresh model
      this.directDateTimeConfig = Object.assign({}, this.directDateTimeConfig);
      this.periodicDateTimeConfig = Object.assign(
        {},
        this.periodicDateTimeConfig
      );
      this.periodicTypeConfig = Object.assign({}, this.periodicTypeConfig);
      this.gradualDateTimeConfig = Object.assign(
        {},
        this.gradualDateTimeConfig
      );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  getCampaignData(data: GetCampaignByIdDto) {
    try {
      if (Object.keys(data).length === 0) return;

      this.isExpandDirectDateTime = false;
      CampaignInfoService.lastCampaignInfo = {};

      this.baseModel.smsBody = data.smsText;
      this.baseModel.senderNumberId = data.privateNumberId;
      this.baseModel.recipientsJson = data.recipientsJson;
      this.baseModel.title = data.title;

      // this.baseModel.campaignId
      // this.baseModel.description=data.description;
      switch (data.sendSubType) {
        // DirectSend
        case SendSubTypes.Direct:
          if (this.showInvalidSendDateTimeError(data.dateTimeNext))
            this.directDateTimeConfig.dateTimeValue = data.dateTimeNext;
          this.onChangeSendType(SendDateTimeType.DateTime);
          this.isExpandDirectDateTime = true;
          break;
        // PeriodicSend
        case SendSubTypes.Periodic:
          if (this.showInvalidSendDateTimeError(data.dateTimeNext)) {
            this.periodicDateTimeConfig.startDateTimeValue = data.dateTimeNext;
            this.periodicDateTimeConfig.endDateTimeValue = data.dateTimeEnd;
          }
          this.periodicTypeConfig.periodText = data.period;
          this.periodicTypeConfig.periodType = data.periodType;
          this.onChangeSendType(SendDateTimeType.Period);
          this.isExpandPeriodDateTime = true;
          break;
        // GradualSend
        case SendSubTypes.Gradual:
          if (this.showInvalidSendDateTimeError(data.dateTimeNext))
            this.gradualDateTimeConfig.dateTimeValue = data.dateTimeNext;

          const timeOut = setTimeout(() => {
            this.baseModel.gradualPeriodText = data.period;
            clearInterval(timeOut);
          }, 100);
          this.baseModel.gradualPageSize = data.pageSize;
          this.onChangeSendType(SendDateTimeType.Gradual);
          this.isExpandGradualDateTime = true;
          break;
      }

      //refresh model
      this.directDateTimeConfig = Object.assign({}, this.directDateTimeConfig);
      this.periodicDateTimeConfig = Object.assign(
        {},
        this.periodicDateTimeConfig
      );
      this.periodicTypeConfig = Object.assign({}, this.periodicTypeConfig);
      this.gradualDateTimeConfig = Object.assign(
        {},
        this.gradualDateTimeConfig
      );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  showInvalidSendDateTimeError(dateString): boolean {
    try {
      const todayDateString = DateTimeService.getTodayDateTime();
      const todayDate = new Date(todayDateString);
      let date = new Date(dateString);

      if (date < todayDate) {
        const invalidDateErrMsg = this.translate?.instant(
          'messages.invalidDateErrorMsgInSend'
        );

        this.dialogService.openDialog({
          title: 'common.error',
          message: invalidDateErrMsg,
          status: DialogStatus.error,
        });

        return false;
      }

      return true;

      // if (this.form.get('sendTypeInformation').get('sendDate').errors?.matDatepickerMin) {
      //   this.directDateTimeConfig.dateTimeValue = "";
      //   this.directDateTimeConfig = Object.assign({}, this.directDateTimeConfig);
      // }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  onShowAlertInEditMode() {
    try {
      let title = this.translate?.instant('common.warning');
      let message = this.translate?.instant(
        'pages.sendingSystem.base.baseSendSMS.message'
      );

      const confirmDialogConfig: DialogConfig = {
        message: message,
        title: title,
        status: DialogStatus.warning,
      };

      this.dialogService.openDialog(confirmDialogConfig);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#endregion

  //#region sendType

  onChangeSendType(type: SendDateTimeType) {
    const timeout = setTimeout(() => {
      this.baseModel.sendDateTimeType = type;
      this.emitModel();
      this.setSendTypeValidations(type);
      clearTimeout(timeout);
    }, 100);
  }

  setSendTypeValidations(type: SendDateTimeType) {
    try {
      //clear
      this.clearSendTypeValidations();

      //#region
      if (type === SendDateTimeType.DateTime) {
        this.form
          .get('sendTypeInformation')
          .get('sendDate')
          .setValidators(Validators.required);
        this.form
          .get('sendTypeInformation')
          .get('sendTime')
          .setValidators(Validators.required);
      } else if (type === SendDateTimeType.Period) {
        this.form
          .get('sendTypeInformation')
          .get('sendPeriodDateStart')
          .setValidators(Validators.required);
        this.form
          .get('sendTypeInformation')
          .get('sendPeriodTimeStart')
          .setValidators(Validators.required);
        this.form
          .get('sendTypeInformation')
          .get('sendPeriodDateEnd')
          .setValidators(Validators.required);
        this.form
          .get('sendTypeInformation')
          .get('sendPeriodTimeEnd')
          .setValidators(Validators.required);
        this.form
          .get('sendTypeInformation')
          .get('periodText')
          .setValidators([Validators.required, Validators.min(1)]);
        this.form
          .get('sendTypeInformation')
          .get('periodTypeValue')
          .setValidators(Validators.required);
      } else if (type === SendDateTimeType.Gradual) {
        this.form
          .get('sendTypeInformation')
          .get('gradualPeriodText')
          .setValidators([Validators.required, Validators.min(1)]);
        this.form
          .get('sendTypeInformation')
          .get('gradualPageSize')
          .setValidators([Validators.required, minNumberValidator(1000)]);
        this.form
          .get('sendTypeInformation')
          .get('sendGradualTimeStart')
          .setValidators(Validators.required);
        this.form
          .get('sendTypeInformation')
          .get('sendGradualDateStart')
          .setValidators(Validators.required);
      }
      //#endregion

      //update
      this.updateSendTypeValidations();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  clearSendTypeValidations() {
    try {
      Object.keys(
        (<FormGroup>this.form.get('sendTypeInformation')).controls
      ).forEach((key) => {
        this.form.get('sendTypeInformation').get(key).clearValidators();
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  updateSendTypeValidations() {
    try {
      Object.keys(
        (<FormGroup>this.form.get('sendTypeInformation')).controls
      ).forEach((key) => {
        this.form.get('sendTypeInformation').get(key).updateValueAndValidity();
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#endregion

  onSubmit() {
    try {
      this.form.markAllAsTouched();
      this.childForm.markAllAsTouched();
      if (!this.isValidForm(this.form, this.tagName, this.formId)) return;
      if (!this.isValidForm(this.childForm, this.childTagName)) return;

      this.emitSubmit();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  emitModel() {
    this.output_model.emit(this.baseModel);
  }

  emitSubmit() {
    this.output_onSubmit.emit(true);
  }

  emitFastSend() {
    this.output_onFastSend.emit(true);
  }

  emitClosePage() {
    this.output_onClosePage.emit(true);
  }

  emitSaveToCampaign() {
    this.output_onSaveToCampaign.emit(this.saveToCampaign);
  }

  openPage(tabName: string) {
    try {
      this.dynamicTabService.setNewTab('');
      this.dynamicTabService.setNewTab(tabName);
      this.dynamicTabService.setActiveTab(tabName);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  unSubs() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onReset() {
    if (this.config?.isEdit) this.getEditingData(this.editModel);
  }

  onShowHelpPage() {
    this.isVisibleHelpPage = true;
  }

  onHideHelpPage() {
    this.isVisibleHelpPage = false;
  }

  onChangeSaveToCampaign() {
    if (this.saveToCampaign) this.emitSaveToCampaign();
  }

  onLogForm() {
    this.cd.detectChanges();
  }

  logExpand(eventName: string, event) {
    console.log(eventName, event);
  }

  getExtraReceivers(event) {
    try {
      this.baseModel.extraReceivers = event;
      if (event)
        this.baseModel.zipExtraReceivers = compressToEncodedURIComponent(event);
      else this.baseModel.zipExtraReceivers = '';
      this.emitModel();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  checkIsValidGlobalIndex(item: GetPhonebookRecipientByScheduledMessageIdDto) {
    try {
      //todo change this line
      // let phonebookRecipient = this.baseModel.phonebookRecipients.find(
      //   (recipient) => recipient.id == item.id
      // );
      // phonebookRecipient = item;
      this.emitModel();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#region check index
  changeStartFrom(item: any, totalCount: number, event) {
    let inputElement = <HTMLInputElement>event?.target;

    if (inputElement) {
      item.fromIndex = this.getNumber(event?.target?.value);
      const formField = document.querySelector(
        `[data-from-index-input-id='${item.id}']`
      );

      item?.fromIndex == 0 || item?.fromIndex > totalCount
        ? this.addErrorClassToInput(formField)
        : item?.fromIndex + item.count > totalCount + 1
        ? this.addErrorClassToBothInput(item.id)
        : this.removeErrorClassFromBothInput(item.id);
    }
  }

  changeCount(item: any, totalCount: number, event) {
    let inputElement = <HTMLInputElement>event?.target;

    if (inputElement) {
      item.count = this.getNumber(event?.target?.value);

      const formField = document.querySelector(
        `[data-count-input-id='${item.id}']`
      );

      item?.count == 0 || item?.count > totalCount
        ? this.addErrorClassToInput(formField)
        : item?.fromIndex + item.count > totalCount + 1
        ? this.addErrorClassToBothInput(item.id)
        : this.removeErrorClassFromBothInput(item.id);
    }
  }

  async addErrorClassToBothInput(id) {
    let fromIndexInput = await this.getFromIndexInputFromDOM(id);
    let countInput = await this.getCountInputFromDOM(id);
    this.addErrorClassToInput(fromIndexInput);
    this.addErrorClassToInput(countInput);
  }

  async removeErrorClassFromBothInput(id) {
    let fromIndexInput = await this.getFromIndexInputFromDOM(id);
    let countInput = await this.getCountInputFromDOM(id);
    this.removeErrorClassFromInput(fromIndexInput);
    this.removeErrorClassFromInput(countInput);
  }

  async getFromIndexInputFromDOM(id: number): Promise<HTMLInputElement> {
    let countInputElement: HTMLInputElement;

    const formIndexInputs = document.querySelectorAll(
      '[data-from-index-input-id]'
    );
    formIndexInputs.forEach((inputElement) => {
      const DataAttr = inputElement.getAttribute('data-from-index-input-id');
      if (+DataAttr == id) {
        countInputElement = inputElement as HTMLInputElement;
      }
    });

    return countInputElement;
  }

  async getCountInputFromDOM(phonebookId: number): Promise<HTMLInputElement> {
    let fromIndexInputElement: HTMLInputElement;

    const toIndexInputs = document.querySelectorAll('[data-count-input-id]');

    toIndexInputs.forEach((inputElement) => {
      const DataAttr = inputElement.getAttribute('data-count-input-id');
      if (+DataAttr == phonebookId) {
        fromIndexInputElement = inputElement as HTMLInputElement;
      }
    });
    return fromIndexInputElement;
  }

  addErrorClassToInput(inputElement) {
    if (!inputElement) return;
    if (
      !(inputElement as HTMLInputElement).classList?.contains(
        'mat-form-field-invalid'
      )
    ) {
      (inputElement as HTMLInputElement).classList?.add(
        'mat-form-field-invalid'
      );
      if (!this.isValidRecipients()) this.isWrongIndex = true;
    }
  }

  isValidRecipients(): boolean {
    let result = true;

    let inputElements = document.querySelectorAll('.indexing');
    inputElements.forEach((el) => {
      if (el?.classList.contains('mat-form-field-invalid')) {
        result = false;
        return;
      }
    });

    return result;
  }

  removeErrorClassFromInput(inputElement) {
    if (!inputElement) return;
    if (
      (inputElement as HTMLInputElement).classList?.contains(
        'mat-form-field-invalid'
      )
    ) {
      (inputElement as HTMLInputElement).classList?.remove(
        'mat-form-field-invalid'
      );
      if (this.isValidRecipients()) this.isWrongIndex = false;
    }
  }

  getNumber(num) {
    if (typeof num == 'number') {
      return num;
    }
    let number: Number = +num?.toString()?.getJustNumber();
    if (number) return +number;
    else return 0;
  }
  //#endregion

  changeRecipients(recipients) {
    this.baseModel.phonebookRecipients = recipients;
    this.emitModel();

    console.log('fsfs', recipients);
  }
}

export interface BaseSendSms {
  senderNumberId?: number;
  smsBody?: string;
  voiceMessageId?: number;
  maxTryCount?: number;
  minuteBetweenTries?: number;
  socialMediaFileId?: number;
  socialMediaFileTitle?: string;
  socialMediaApplicationId?: number;
  socialMediaApplicationName?: string;
  finalSmsBody?: string;
  smsCount?: number;
  smsPart?: number;
  campaignId?: number;
  title?: string;
  recipientsJson?: string;
  isSendWhiteList?: boolean;
  isFlashSms?: boolean;
  fastSendReceivers?: string;
  sendDateTimeType?: SendDateTimeType;
  directSendDateTime?: string;
  gradualSendDateTime?: string;
  periodicSendStartDateTime?: string;
  periodicSendEndDateTime?: string;
  periodTypeValue?: SmsSentPeriodType;
  gradualPeriodText?: number;
  gradualPageSize?: number;
  description?: string;
  voiceMessageDetails?: VoiceMessageDetailsRequest;
  socialMediaMessageDetails?: SocialMediaSendDetailModel;
  isCanSendVoice?: boolean;
  isCanSendSocialMedia?: boolean;
  isCanSendFlashSms?: boolean;
  isTwilioNumber?: boolean;
  extraReceivers?: string;
  zipExtraReceivers?: string;
  phonebookRecipients?: Array<PhonebookRecipientForUpdateRequest>;
}

export interface BaseSendSmsConfig {
  title?: string;
  receivers?: string;
  isShowPageHeader?: boolean;
  isShowExtraReceivers?: boolean;
  isShowCampaign?: boolean;
  msgInfoClassList?: string;
  receiversClassList?: string;
  sendDateTimeClassList?: string;
  isShowReceivers?: boolean;
  isCustomMessageInformation?: boolean;
  isShowFastSend?: boolean;
  isEdit?: boolean;
  isConvertToIsoString?: boolean;
  isAddSignature?: boolean;
  isAddHeader?: boolean;
  sendType?: SendTypes;
  useType?: UseTypes;
  privateNumberType?: AppPrivateNumber;
  justShowDirectDateTimeForEdit?: boolean;
}
